import axios from 'axios';
import { supabase } from './supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Log API configuration on initialization (only in non-production for debugging)
if (process.env.NODE_ENV !== 'production') {
  console.log('[API] BACKEND_URL:', BACKEND_URL);
  console.log('[API] API_BASE:', API_BASE);
}

// Demo mode state (will be set by AuthContext)
let isDemoMode = false;
const DEMO_USER_ID = "demo-geni-account-001";

// Shared profile mode state (will be set by SharedProfileContext) - legacy share_id based
let sharedProfileId = null;

// Public profile mode state (will be set by SharedProfileContext) - new user_id based
let publicProfileUserId = null;

// Single-flight Promise Lock para resolução coordenada de sessão/refresh do token do Supabase
let sessionPromise = null;

const getCoordinatedSession = () => {
  if (sessionPromise) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[API] Reusing pending session resolution Promise');
    }
    return sessionPromise;
  }
  
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Creating new coordinated session resolution Promise');
  }
  
  sessionPromise = supabase.auth.getSession().then(
    ({ data: { session } }) => {
      sessionPromise = null;
      return session;
    },
    (error) => {
      sessionPromise = null;
      throw error;
    }
  );
  
  return sessionPromise;
};

export const setDemoMode = (enabled) => {
  isDemoMode = enabled;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Demo mode:', enabled ? 'enabled' : 'disabled');
  }
};

export const getDemoMode = () => isDemoMode;

// Shared profile mode controls (legacy - share_id based)
export const setSharedProfileMode = (shareId) => {
  sharedProfileId = shareId;
  publicProfileUserId = null; // Clear public profile mode
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Shared profile mode:', shareId ? `enabled (${shareId})` : 'disabled');
  }
};

export const clearSharedProfileMode = () => {
  sharedProfileId = null;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Shared profile mode: disabled');
  }
};

export const getSharedProfileId = () => sharedProfileId;

export const isInSharedProfileMode = () => sharedProfileId !== null;

// Public profile mode controls (new - persistent, user_id based)
export const setPublicProfileMode = (userId) => {
  publicProfileUserId = userId;
  sharedProfileId = null; // Clear legacy share mode
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Public profile mode:', userId ? `enabled (${userId})` : 'disabled');
  }
};

export const clearPublicProfileMode = () => {
  publicProfileUserId = null;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[API] Public profile mode: disabled');
  }
};

export const getPublicProfileUserId = () => publicProfileUserId;

export const isInPublicProfileMode = () => publicProfileUserId !== null;

// Admin "view as user" mode (Beta support tool) - read-only, header-based, mirrors the
// public/shared profile pattern above. Persisted in sessionStorage so it survives a
// page refresh but never leaks into a new browser tab/session.
const ADMIN_VIEW_STORAGE_KEY = 'geni_admin_view_as';
let adminView = null;
try {
  const storedAdminView = sessionStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
  adminView = storedAdminView ? JSON.parse(storedAdminView) : null;
} catch (e) {
  adminView = null;
}

export const setAdminViewMode = (userId, email) => {
  adminView = { userId, email };
  try {
    sessionStorage.setItem(ADMIN_VIEW_STORAGE_KEY, JSON.stringify(adminView));
  } catch (e) {
    // sessionStorage unavailable - admin view still works for this tab via the in-memory value
  }
};

export const clearAdminViewMode = () => {
  adminView = null;
  try {
    sessionStorage.removeItem(ADMIN_VIEW_STORAGE_KEY);
  } catch (e) {
    // no-op
  }
};

export const getAdminViewUserId = () => adminView?.userId || null;
export const getAdminViewEmail = () => adminView?.email || null;
export const isInAdminViewMode = () => Boolean(adminView?.userId);

// Check if in any profile viewing mode (shared or public)
export const isInProfileViewMode = () => sharedProfileId !== null || publicProfileUserId !== null;

// Create axios instance with interceptors for authentication
const api = axios.create({
  baseURL: API_BASE,
});

// Public API instance - for endpoints that don't require authentication
// Use this for: /public/* endpoints
export const publicApi = axios.create({
  baseURL: API_BASE,
});

// Request interceptor to add auth token and mode headers
api.interceptors.request.use(
  async (config) => {
    // Demo mode takes precedence (shouldn't happen simultaneously, but just in case)
    if (isDemoMode) {
      config.headers['X-Demo-Mode'] = 'true';
      config.headers['X-User-ID'] = DEMO_USER_ID;
      return config;
    }
    
    // Public profile mode - add header but DON'T require auth
    // Backend will validate the public profile and return data without auth
    if (publicProfileUserId) {
      config.headers['X-Public-Profile-ID'] = publicProfileUserId;
      
      // Try to add auth if available (for logged-in viewers), but don't require it
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        }
      } catch (e) {
        // Auth not available - that's OK for public profile viewing
        console.log('[API] Public profile mode without auth');
      }
      return config;
    }
    
    // Get auth session (required for normal and shared profile modes) de forma coordenada
    const session = await getCoordinatedSession();
    
    // Block request if no session
    if (!session?.access_token) {
      const error = new Error('No authentication session');
      error.code = 'NO_AUTH_SESSION';
      throw error;
    }
    
    // Add auth header
    config.headers.Authorization = `Bearer ${session.access_token}`;

    // Admin "view as user" mode - the admin keeps their own auth session, this header
    // just tells the backend which user's data to read (backend enforces admin-only + read-only)
    if (adminView?.userId) {
      config.headers['X-Admin-View-User-ID'] = adminView.userId;
    }
    
    // Add shared profile header if in shared profile mode (legacy)
    if (sharedProfileId) {
      config.headers['X-Shared-Profile-ID'] = sharedProfileId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't redirect for cancelled requests due to no auth
    if (error.code === 'NO_AUTH_SESSION') {
      return Promise.reject(error);
    }
    
    // Don't redirect on auth errors in demo mode
    if (isDemoMode) {
      return Promise.reject(error);
    }
    
    // Don't redirect on auth errors in profile view modes (handled by wrappers)
    if (sharedProfileId || publicProfileUserId) {
      return Promise.reject(error);
    }

    // Admin support view keeps the admin's real auth session and adds only the
    // target user header. In that mode, backend 403s are expected for read-only
    // protections and must NOT be interpreted as an auth/session failure.
    if (adminView?.userId && error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Falha transitória do serviço de auth upstream (Supabase timeout/erro momentâneo):
    // 1 única retentativa com pequeno delay, sem loop. Isso evita que um soluço
    // passageiro do Supabase derrube permanentemente a request (ex: Setups/Open
    // Positions aparecendo vazios só porque essa request específica bateu no
    // instante errado).
    if (error.response?.status === 503 && originalRequest && !originalRequest._retryUpstream) {
      originalRequest._retryUpstream = true;
      await new Promise((resolve) => setTimeout(resolve, 800));
      return api(originalRequest);
    }

    // Se receber 401 ou 403 do backend, tentar 1 única vez atualizar a sessão e repetir a requisição
    if ((error.response?.status === 401 || error.response?.status === 403) && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[API] Attempting coordinated session refresh and request retry after 401/403');
        }
        const session = await getCoordinatedSession();
        if (session?.access_token) {
          originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
          return api(originalRequest);
        }
      } catch (retryErr) {
        console.error('[API] Error during coordinated session refresh retry:', retryErr);
      }
      
      // Procede para logout caso o refresh falhe ou continue não autorizado
      clearAdminViewMode();
      await supabase.auth.signOut();
      window.location.href = '/login';
    } else if (error.response?.status === 401 || error.response?.status === 403) {
      clearAdminViewMode();
      await supabase.auth.signOut();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE };
